/**
 * Utility functions related to searching the careers.
 */

import { SearchApi } from 'global/api';
import {
  CareerAppliedFilter,
  CareerAppliedFilterFilterOnEnum,
  CareerSearchRequest,
  CareerSearchRequestOrderingEnum,
} from 'labxchange-client';

export interface CareerTagData {
  id: string;
  name: string;
  type: CareerAppliedFilterFilterOnEnum;
  careerCount?: number;
  childTags?: CareerTagData[];
  opened?: boolean;
}

export function buildParentChildFilterOptions(
  aggregations: any[],
  filterType: CareerAppliedFilterFilterOnEnum
): CareerTagData[] {
  const filterTags: CareerTagData[] = [];
  for (const { key, doc_count } of aggregations) {
    // key is either a parent/child key like "Biological Sciences:Genetics"
    // or a parent key like "Biological Sciences"
    const tagId = `${filterType}:${key}`;
    const isChild = key.indexOf(':') !== -1; // Is this a child or a parent?
    if (isChild) {
      const name = key.split(':')[1];
      const parentTagName = key.split(':')[0];
      const parentTagId = filterType + ':' + parentTagName;
      let parentTag = filterTags.find((tag) => tag.id === parentTagId);
      if (parentTag === undefined) {
        parentTag = {
          id: parentTagId,
          name: parentTagName,
          type: filterType,
          childTags: [],
          careerCount: undefined,
          opened: false,
        };
        filterTags.push(parentTag);
      }
      parentTag.childTags.push({
        id: tagId,
        name,
        type: filterType,
        careerCount: doc_count,
      });
    } else {
      // If this parent tag wasn't already added by a child tag, add it now:
      if (filterTags.find((tag) => tag.id === tagId) === undefined) {
        filterTags.push({
          id: tagId,
          name: key,
          type: filterType,
          careerCount: doc_count,
          childTags: [],
          opened: false,
        });
      }
    }
  }

  return filterTags;
}

export function buildSimpleFilterOptions(
  aggregationsData: any[],
  filterType: CareerAppliedFilterFilterOnEnum
): CareerTagData[] {
  const careerTags: CareerTagData[] = [];
  for (const { key, doc_count } of aggregationsData) {
    careerTags.push({
      id: key,
      name: key,
      type: filterType,
      careerCount: doc_count,
      opened: false,
    });
  }
  return careerTags;
}

export const fetchCareersData = async (
  selectedQualification: CareerTagData[],
  selectedSubjects: CareerTagData[],
  selectedFields: CareerTagData[],
  sortOrder: CareerSearchRequestOrderingEnum,
  value?: string,
  page: number = 1
) => {
  const filters: CareerAppliedFilter[] = [];

  const addFilter = (
    items: CareerTagData[],
    filterOn: CareerAppliedFilterFilterOnEnum
  ) => {
    if (items.length > 0) {
      filters.push({
        filterOn,
        filterValues: items.map((item) => {
          const parts = item.id.split(`${filterOn}:`);
          if (parts.length === 1) {
            return parts[0];
          }
          return parts[1];
        }),
      });
    }
  };

  addFilter(
    selectedQualification,
    CareerAppliedFilterFilterOnEnum.MinimumQualification
  );
  addFilter(selectedSubjects, CareerAppliedFilterFilterOnEnum.SubjectArea);
  addFilter(selectedFields, CareerAppliedFilterFilterOnEnum.Category);

  const searchRequest: CareerSearchRequest = {
    filters,
    keywords: value,
    currentPage: page,
    paginationSize: 16,
    ordering: sortOrder,
  };

  return SearchApi.careerSearch({ data: searchRequest });
};
